/**
 * useBackgroundCheck — pulls the most recent background_checks row
 * for the current member, subscribes to realtime updates, and exposes
 * a `start()` action that hits the request-bgc-scan edge function.
 *
 * Presentation only.  Eligibility + rate limiting + status mutations
 * are enforced in the edge function and the fn_request_bgc RPC.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type BgcStatus =
  | "not_started"
  | "requested"
  | "in_progress"
  | "requires_input"
  | "passed"
  | "failed"
  | "expired"
  | "canceled";

export interface BackgroundCheckRow {
  id: string;
  user_id: string;
  provider: string;
  external_ref: string | null;
  status: BgcStatus;
  result_summary: Record<string, unknown>;
  failure_reason: string | null;
  requested_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  attempts: number;
  last_attempt_at: string | null;
  created_at: string;
  updated_at: string;
}

interface StartResult {
  row?: BackgroundCheckRow;
  error?: string;
  rateLimited?: boolean;
  notEntitled?: boolean;
}

export const useBackgroundCheck = () => {
  const { user } = useAuth();
  const [row, setRow] = useState<BackgroundCheckRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setRow(null);
      setLoading(false);
      return;
    }
    const { data } = await (supabase as any)
      .from("background_checks")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setRow((data as BackgroundCheckRow | null) ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Realtime: vendor webhook flips status → reflect immediately.
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`bgc:${user.id}:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "background_checks",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refresh();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, refresh]);

  const start = useCallback(async (): Promise<StartResult> => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke(
      "request-bgc-scan",
      { body: { kind: "bgc" } },
    );
    setBusy(false);
    if (error) {
      const ctx = (error as { context?: { status?: number } }).context;
      if (ctx?.status === 429) return { rateLimited: true };
      if (ctx?.status === 402) return { notEntitled: true };
      return { error: error.message };
    }
    const payload = data as { row?: BackgroundCheckRow; error?: string } | null;
    if (payload?.row) {
      setRow(payload.row);
      return { row: payload.row };
    }
    return { error: payload?.error ?? "unknown_error" };
  }, []);

  // Convenience flags
  const isPassed = row?.status === "passed";
  const isInProgress = row?.status === "requested" || row?.status === "in_progress";
  const isFailed = row?.status === "failed" || row?.status === "expired";
  const needsAction = row?.status === "requires_input";

  return {
    row,
    loading,
    busy,
    isPassed,
    isInProgress,
    isFailed,
    needsAction,
    refresh,
    start,
  };
};
